import { ref, onMounted, onUnmounted, watchEffect, type Ref } from 'vue'

interface Options {
  zonePercentage?: number
  scrollSpeed?: number
}

export function useHoverScroll(
  targetRef: Ref<HTMLElement | null>,
  options: Options = {},
): { isInLeftZone: Ref<boolean>; isInRightZone: Ref<boolean> } {
  const { zonePercentage = 8, scrollSpeed = 3 } = options

  const isInLeftZone = ref<boolean>(false)
  const isInRightZone = ref<boolean>(false)

  let animationFrameId: number | null = null

  const handleMouseMove = (event: MouseEvent) => {
    if (!targetRef.value) return

    const rect = targetRef.value.getBoundingClientRect()
    const mouseX = event.clientX - rect.left

    const leftZoneWidth = rect.width * (zonePercentage / 100)
    const rightZoneStart = rect.width - leftZoneWidth

    isInLeftZone.value = mouseX >= 0 && mouseX <= leftZoneWidth
    isInRightZone.value = mouseX >= rightZoneStart && mouseX <= rect.width
  }

  const handleMouseLeave = () => {
    isInLeftZone.value = false
    isInRightZone.value = false
  }

  const scrollLoop = () => {
    if (!targetRef.value) return

    if (isInLeftZone.value) {
      targetRef.value.scrollLeft -= scrollSpeed
    } else if (isInRightZone.value) {
      targetRef.value.scrollLeft += scrollSpeed
    }

    animationFrameId = requestAnimationFrame(scrollLoop)
  }

  watchEffect(() => {
    if (isInLeftZone.value || isInRightZone.value) {
      scrollLoop()
    } else if (animationFrameId) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }
  })

  onMounted(() => {
    targetRef.value?.addEventListener('mousemove', handleMouseMove)
    targetRef.value?.addEventListener('mouseleave', handleMouseLeave)
  })

  onUnmounted(() => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId)
    }
    targetRef.value?.removeEventListener('mousemove', handleMouseMove)
    targetRef.value?.removeEventListener('mouseleave', handleMouseLeave)
  })

  return { isInLeftZone, isInRightZone }
}
